/* eslint-disable @typescript-eslint/no-shadow */
import { useSelector } from 'react-redux';
import * as R from 'remeda';
import z from 'zod';
import { DEV_FEATURES_ENABLED } from '../../constants/config';
import { userLabConfigSelector } from '../../redux/modules/user/selectors';
import { RossumLabConfig } from '../../types/user';
import { unleashClient } from '../../unleash/Unleash';
import { parseAndValidate } from '../../utils/jsonParse';
import { allFeatures, Feature, FeatureId, isLabFeature } from './features';

export const UNLEASH_OVERRIDES_KEY = 'localUnleashOverrides';

export const overridesSchema = z.record(z.boolean());

export const getLocalFeatureOverrides = (): Record<string, boolean> => {
  if (!DEV_FEATURES_ENABLED) {
    return {};
  }

  return parseAndValidate(
    localStorage.getItem(UNLEASH_OVERRIDES_KEY),
    overridesSchema,
    {}
  );
};

export const getFeatureLocalOverride = (
  featureId: FeatureId
): boolean | null => {
  const overrides = getLocalFeatureOverrides();

  return overrides[featureId] ?? null;
};

export const isFeatureLocallyOverridden = (featureId: FeatureId): boolean => {
  return getFeatureLocalOverride(featureId) !== null;
};

export const isFeatureLocallyEnabled = (featureId: FeatureId): boolean => {
  return !!getFeatureLocalOverride(featureId);
};

export const setFeatureLocalOverride = (
  featureId: FeatureId,
  value: boolean | null
) => {
  if (!DEV_FEATURES_ENABLED) {
    return;
  }

  const overrides = getLocalFeatureOverrides();

  const newValue =
    value === null
      ? R.omitBy(overrides, (_, key) => key === featureId)
      : { ...overrides, [featureId]: value };

  localStorage.setItem(UNLEASH_OVERRIDES_KEY, JSON.stringify(newValue));
};

export const isFeatureGloballyEnabled = (featureId: FeatureId): boolean => {
  const valueFromUnleash = unleashClient.isEnabled(featureId);
  const antivalueFromUnleash = unleashClient.isEnabled(`anti-${featureId}`);

  return valueFromUnleash && !antivalueFromUnleash;
};

const getIsFeatureGenerallyAvailable =
  (publicFeaturesConfig: Partial<Record<FeatureId, Feature>>, date: Date) =>
  (featureId: FeatureId): boolean => {
    const featureConfig = publicFeaturesConfig[featureId];
    // Non-public features have no concept of GA, they are automatically available
    if (!featureConfig || !isLabFeature(featureConfig)) {
      return true;
    }

    // If feature is public, check if date is after gaDate
    const gaDate = new Date(featureConfig.gaDate);

    return date >= gaDate;
  };

export const isFeatureGenerallyAvailable = getIsFeatureGenerallyAvailable(
  allFeatures,
  new Date()
);

const getGetFeatureUserOverride =
  (publicFeaturesConfig: Partial<Record<FeatureId, Feature>>, date: Date) =>
  (featureId: FeatureId, userLabConfig: RossumLabConfig): boolean | null => {
    const userOverride = userLabConfig[featureId];
    const featureConfig = publicFeaturesConfig[featureId];

    if (!userOverride || !featureConfig || !isLabFeature(featureConfig)) {
      return null;
    }

    if (
      !getIsFeatureGenerallyAvailable(publicFeaturesConfig, date)(featureId)
    ) {
      return userOverride.enabled;
    }

    const gaDate = new Date(featureConfig.gaDate);
    const lastUpdatedDate = new Date(userOverride.lastUpdatedAt);

    if (lastUpdatedDate < gaDate) {
      return null;
    }

    return userOverride.enabled;
  };

export const getFeatureUserOverride = getGetFeatureUserOverride(
  allFeatures,
  new Date()
);

export const isFeatureUserOverridden = (
  featureId: FeatureId,
  labConfig: RossumLabConfig
): boolean => {
  return getFeatureUserOverride(featureId, labConfig) !== null;
};

const getIsFeatureEnabled =
  ({
    isFeatureGloballyEnabled = R.constant(true),
    isFeatureGenerallyAvailable = R.constant(true),
    getFeatureLocalOverride = R.constant(null),
    getFeatureUserOverride = R.constant(null),
  }: {
    isFeatureGloballyEnabled?: (featureId: FeatureId) => boolean;
    isFeatureGenerallyAvailable?: (featureId: FeatureId) => boolean;
    getFeatureLocalOverride?: (featureId: FeatureId) => boolean | null;
    getFeatureUserOverride?: (
      featureId: FeatureId,
      userLabConfig: RossumLabConfig
    ) => boolean | null;
  }) =>
  (featureId: FeatureId, userLabConfig: RossumLabConfig) => {
    const localOverride = getFeatureLocalOverride(featureId);
    if (localOverride !== null) {
      return localOverride;
    }

    if (!isFeatureGloballyEnabled(featureId)) {
      return false;
    }

    const userOverride = getFeatureUserOverride(featureId, userLabConfig);

    if (userOverride !== null) {
      return userOverride;
    }

    return isFeatureGenerallyAvailable(featureId);
  };

export const isFeatureEnabled = getIsFeatureEnabled({
  isFeatureGloballyEnabled,
  isFeatureGenerallyAvailable,
  getFeatureLocalOverride,
  getFeatureUserOverride,
});

export const useIsFeatureEnabled = (featureId: FeatureId): boolean => {
  const labConfig = useSelector(userLabConfigSelector);

  return isFeatureEnabled(featureId, labConfig);
};

export const __internal = {
  getIsFeatureGenerallyAvailable,
  getGetFeatureUserOverride,
  getIsFeatureEnabled,
};
